import type {
    ApiResponse,
    CoachAffiliation,
    CoachAffiliationInvitation,
    CoachAssignment,
    CoachAssignmentListResult,
    CoachAssignmentStatus,
    Coach,
    CoachAvailability,
    CoachCommercialPrice,
    CoachContract,
    CoachPayrollSummary,
    CoachSport,
    CoachSportListResult,
    CoachDashboardSummary,
    CoachPlanResult,
    CoachProfile,
    DashboardRange,
    LoginResult,
    RegisterResult,
    Sport,
    VerifyEmailResult,
} from "@flexv-coach/shared";

const BASE_URL = import.meta.env.VITE_API_URL ?? "/api";

const ACCESS_TOKEN_KEY = "flexv-coach.access-token";
const REFRESH_TOKEN_KEY = "flexv-coach.refresh-token";

export const tokenStore = {
    getAccess: () => localStorage.getItem(ACCESS_TOKEN_KEY),
    getRefresh: () => localStorage.getItem(REFRESH_TOKEN_KEY),
    set(access: string, refresh: string) {
        localStorage.setItem(ACCESS_TOKEN_KEY, access);
        localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
    },
    clear() {
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
    },
};

export class ApiError extends Error {
    constructor(
        message: string,
        readonly status: number,
        readonly details?: unknown,
    ) {
        super(message);
    }
}

type QueryValue = string | number | boolean | undefined;

interface RequestOptions {
    method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
    body?: unknown;
    query?: Record<string, QueryValue>;
    signal?: AbortSignal;
}

function buildUrl(path: string, query?: Record<string, QueryValue>): string {
    const url = `${BASE_URL}${path}`;
    if (!query) return url;
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
        if (value !== undefined && value !== "") params.set(key, String(value));
    }
    const qs = params.toString();
    return qs ? `${url}?${qs}` : url;
}

async function parse<T>(response: Response): Promise<T> {
    const text = await response.text();
    const payload = text ? (JSON.parse(text) as ApiResponse<T>) : null;

    if (!response.ok || !payload || payload.success === false) {
        const message =
            (payload && !payload.success && payload.error) ||
            `Request failed with status ${response.status}`;
        const details = payload && !payload.success ? payload.details : undefined;
        throw new ApiError(message, response.status, details);
    }

    return payload.data;
}

let refreshInFlight: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
    const refreshToken = tokenStore.getRefresh();
    if (!refreshToken) throw new ApiError("No session", 401);

    const response = await fetch(buildUrl("/coach-auth/refresh"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
    });
    const result = await parse<LoginResult>(response);
    tokenStore.set(result.token, result.refreshToken);
    return result.token;
}

async function request<T>(path: string, options: RequestOptions = {}, isRetry = false): Promise<T> {
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    const accessToken = tokenStore.getAccess();
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

    const response = await fetch(buildUrl(path, options.query), {
        method: options.method ?? "GET",
        headers,
        body: options.body === undefined ? undefined : JSON.stringify(options.body),
        signal: options.signal,
    });

    if (response.status === 401 && !isRetry && tokenStore.getRefresh()) {
        try {
            if (!refreshInFlight) {
                refreshInFlight = refreshAccessToken().finally(() => {
                    refreshInFlight = null;
                });
            }
            await refreshInFlight;
            return request<T>(path, options, true);
        } catch {
            tokenStore.clear();
            throw new ApiError("Session expired", 401);
        }
    }

    return parse<T>(response);
}

/* -------------------------------------------------------------------------- */
/* Auth                                                                       */
/* -------------------------------------------------------------------------- */

/** Payload for self-service coach signup (`POST /coach-auth/register`). */
export interface RegisterPayload {
    fullName: string;
    phone: string;
    email: string;
    password: string;
    /** Set when the coach arrived from an unlinked Google sign-in attempt. */
    googleIdToken?: string;
}

export const authApi = {
    login: async (identifier: string, password: string): Promise<LoginResult> => {
        const result = await request<LoginResult>("/coach-auth/login", {
            method: "POST",
            body: { identifier, password },
        });
        tokenStore.set(result.token, result.refreshToken);
        return result;
    },

    google: async (idToken: string): Promise<LoginResult> => {
        const result = await request<LoginResult>("/coach-auth/google", {
            method: "POST",
            body: { idToken },
        });
        tokenStore.set(result.token, result.refreshToken);
        return result;
    },

    /**
     * Creates the coach account — OTP-dulu: manual tanpa sesi, frontend
     * wajib ke /verify-email. Google signup langsung verified + sesi.
     * Token hanya disimpan bila backend menerbitkannya.
     */
    register: async (payload: RegisterPayload): Promise<RegisterResult> => {
        const result = await request<RegisterResult>("/coach-auth/register", {
            method: "POST",
            body: payload,
        });
        if (result.token && result.refreshToken) {
            tokenStore.set(result.token, result.refreshToken);
        }
        return result;
    },

    me: (signal?: AbortSignal) => request<CoachProfile>("/coach-auth/me", { signal }),

    /**
     * Verifies the 6-digit email code sent via Company System. Verify fresh
     * menerbitkan sesi pertama (auto-login) — token langsung disimpan.
     * `alreadyVerified` tanpa sesi: login via /login.
     */
    verifyEmail: async (email: string, code: string): Promise<VerifyEmailResult> => {
        const result = await request<VerifyEmailResult>("/coach-auth/verify-email", {
            method: "POST",
            body: { email, code },
        });
        if (result.token && result.refreshToken) {
            tokenStore.set(result.token, result.refreshToken);
        }
        return result;
    },

    /** Requests a new verification code (rate-limited server-side). */
    resendOtp: (email: string) =>
        request<{ expiresAt: string; alreadyVerified: boolean }>("/coach-auth/resend-otp", {
            method: "POST",
            body: { email },
        }),

    logout: async (): Promise<void> => {
        const refreshToken = tokenStore.getRefresh();
        try {
            if (refreshToken) {
                await request("/coach-auth/logout", {
                    method: "POST",
                    body: { refreshToken },
                });
            }
        } finally {
            tokenStore.clear();
        }
    },
};

/* -------------------------------------------------------------------------- */
/* Coach plan                                                                 */
/* -------------------------------------------------------------------------- */

export interface PlanQuery {
    from: string;
    to: string;
    outletId?: string;
    status?: CoachAssignmentStatus | "";
    sourceType?: string;
}

export const planApi = {
    get: (query: PlanQuery, signal?: AbortSignal) =>
        request<CoachPlanResult>("/coach/plan", { query: { ...query }, signal }),

    updateStatus: (
        assignmentId: string,
        status: CoachAssignmentStatus,
        notes?: string,
    ): Promise<CoachAssignment> =>
        request<CoachAssignment>(`/coach/plan/assignments/${assignmentId}/status`, {
            method: "PATCH",
            body: { status, notes },
        }),
};

/* -------------------------------------------------------------------------- */
/* Supporting coach data                                                      */
/* -------------------------------------------------------------------------- */

export const coachApi = {
    availability: (signal?: AbortSignal) =>
        request<CoachAvailability[]>("/coach/availability", { signal }),

    contracts: (signal?: AbortSignal) =>
        request<CoachContract[]>("/coach/contracts", { signal }),

    affiliations: (signal?: AbortSignal) =>
        request<CoachAffiliation[]>("/coach/affiliations", { signal }),

    /**
     * Ubah data tampilan sendiri (`displayName`, `phone`, `bio`). Email &
     * nama login bukan bagian payload — backend menolaknya (400).
     */
    updateProfile: (payload: {
        displayName?: string;
        phone?: string;
        bio?: string | null;
    }): Promise<Coach> =>
        request<Coach>("/coach/me", { method: "PATCH", body: payload }),
};

/* -------------------------------------------------------------------------- */
/* Coach assignment (menu Assignment)                                         */
/* -------------------------------------------------------------------------- */

export interface AssignmentListQuery {
    from: string;
    to: string;
    outletId?: string;
    status?: CoachAssignmentStatus | "";
    sourceType?: string;
    upcomingOnly?: boolean;
}

export const assignmentsApi = {
    get: (query: AssignmentListQuery, signal?: AbortSignal) =>
        request<CoachAssignmentListResult>("/coach/assignments", {
            query: { ...query },
            signal,
        }),
};

/* -------------------------------------------------------------------------- */
/* Master sports — setara `GET /sport` Flexv Mobile API                       */
/* -------------------------------------------------------------------------- */

export const sportsApi = {
    list: (signal?: AbortSignal) => request<Sport[]>("/sports", { signal }),
};

/* -------------------------------------------------------------------------- */
/* Coach sports (menu Sport)                                                  */
/* -------------------------------------------------------------------------- */

export const coachSportsApi = {
    list: (signal?: AbortSignal) =>
        request<CoachSportListResult>("/coach/sports", { signal }),

    create: (sportId: string, isPrimary?: boolean): Promise<CoachSport> =>
        request<CoachSport>("/coach/sports", {
            method: "POST",
            body: { sportId, isPrimary },
        }),

    setPrimary: (id: string, isPrimary: boolean): Promise<CoachSport> =>
        request<CoachSport>(`/coach/sports/${id}`, {
            method: "PATCH",
            body: { isPrimary },
        }),

    remove: (id: string): Promise<{ id: string }> =>
        request<{ id: string }>(`/coach/sports/${id}`, { method: "DELETE" }),
};

/* -------------------------------------------------------------------------- */
/* Coach dashboard                                                            */
/* -------------------------------------------------------------------------- */

export interface DashboardQuery {
    range: DashboardRange;
    /** ISO date anchor — default hari ini di server. */
    date?: string;
}

export const dashboardApi = {
    get: (query: DashboardQuery, signal?: AbortSignal) =>
        request<CoachDashboardSummary>("/coach/dashboard", { query: { ...query }, signal }),
};

/* -------------------------------------------------------------------------- */
/* Service pricing (menu Pricing)                                             */
/* -------------------------------------------------------------------------- */

export const pricingApi = {
    list: (signal?: AbortSignal) =>
        request<CoachCommercialPrice[]>("/coach/pricing", { signal }),
};

/* -------------------------------------------------------------------------- */
/* Payroll summary (menu Payroll)                                             */
/* -------------------------------------------------------------------------- */

export const payrollApi = {
    summary: (month: string, signal?: AbortSignal) =>
        request<CoachPayrollSummary>("/coach/payroll-summary", {
            query: { month },
            signal,
        }),
};

/* -------------------------------------------------------------------------- */
/* Affiliation invitations (undangan klub, diputuskan coach)                   */
/* -------------------------------------------------------------------------- */

export type InvitationDecision = "accept" | "decline";

export interface InvitationDecisionResult {
    ok: true;
    status: "active" | "rejected";
    /** True bila ini klub pertama coach → otomatis jadi klub asal. */
    becamePrimary: boolean;
}

export const invitationsApi = {
    list: (signal?: AbortSignal) =>
        request<CoachAffiliationInvitation[]>("/coach/affiliation-invitations", {
            signal,
        }),
    decide: (id: string, decision: InvitationDecision) =>
        request<InvitationDecisionResult>(`/coach/affiliation-invitations/${id}/decision`, {
            method: "POST",
            body: { decision },
        }),
};
